import axios from 'axios';

import axiosInstance from '../../lib/axios';
import { presignHeaders } from '../../lib/presign';

export const fetchAssets = async (params = {}) => {
  const response = await axiosInstance.get('/assets/', { params });
  return response.data;
};

export const fetchAudioAssetStatus = async (id) => {
  const response = await axiosInstance.get(`/assets/audio/${id}/status/`);
  return response.data;
};

export const fetchAudioAssetAccess = async (id, ttl) => {
  const response = await axiosInstance.get(`/assets/audio/${id}/access/`, {
    params: ttl ? { ttl } : undefined,
  });
  return response.data;
};

// The backend does not currently expose GET /assets/:id.
// This stub keeps the Redux flow wired and surfaces a clear error in the UI
// until the matching backend route is added.
export const fetchAssetById = async () => {
  throw new Error('Fetching a single asset by ID is not supported by the backend yet.');
};

export const deleteAsset = async (id) => {
  const response = await axiosInstance.delete(`/admin/assets/${id}/`);
  return response.data;
};

export const bulkDeleteAssets = async (assetIds) => {
  const response = await axiosInstance.post('/admin/assets/bulk/delete/', { asset_ids: assetIds });
  return response.data;
};

export const fetchVideoAssetAccess = async (id, ttl) => {
  const response = await axiosInstance.get(`/assets/video/${id}/access/`, {
    params: ttl ? { ttl } : undefined,
  });
  return response.data;
};

/**
 * Upload an audio file via the admin presigned-URL flow (request → direct R2
 * PUT → confirm). Direct-to-R2 keeps slow uploads off the Django worker and
 * survives limited bandwidth; onProgress receives 0-100 during the PUT.
 *
 * `ctx` is a caller-held mutable object. The asset_id, presigned URL and signed
 * headers from the first request are stored on it, so a retry after a failed
 * PUT/confirm reuses the same pending asset instead of minting a new one each attempt.
 */
export const uploadAudioAsset = async (file, onProgress, ctx = {}) => {
  if (!ctx.assetId || !ctx.presignedUrl) {
    const { data: requestData } = await axiosInstance.post('/admin/assets/upload/request/', {
      filename: file.name,
      asset_type: 'audio',
      content_type: file.type || 'audio/mpeg',
      asset_name: file.name,
    });
    ctx.presignedUrl = requestData.presignedUrl || requestData.presigned_url;
    ctx.assetId = requestData.assetId || requestData.asset_id;
    ctx.headers = presignHeaders(requestData, file.type || 'audio/mpeg');
  }

  await axios.put(ctx.presignedUrl, file, {
    headers: {
      ...ctx.headers,
      'x-amz-meta-asset_id': ctx.assetId,
      'x-amz-meta-original_filename': file.name,
    },
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });

  const { data: confirmData } = await axiosInstance.post('/admin/assets/upload/confirm/', {
    asset_id: ctx.assetId,
  });
  return confirmData;
};

/**
 * Upload an image asset via the admin presigned-URL flow:
 *   1. POST /admin/assets/upload/request/ - get presigned URL (admin role required)
 *   2. PUT the file directly to the presigned URL (no auth headers)
 *   3. POST /admin/assets/upload/confirm/ - finalise the asset
 */
export const uploadImageAsset = async (file, assetName, assetType = 'image') => {
  const { data: requestData } = await axiosInstance.post('/admin/assets/upload/request/', {
    filename: file.name,
    asset_type: assetType,
    content_type: file.type,
    asset_name: assetName || file.name,
  });

  const presignedUrl = requestData.presignedUrl || requestData.presigned_url;
  const assetId = requestData.assetId || requestData.asset_id;
  const uploadKey = requestData.uploadKey || requestData.upload_key;

  await axios.put(presignedUrl, file, {
    headers: {
      ...presignHeaders(requestData, file.type),
      'x-amz-meta-asset_id': assetId,
      'x-amz-meta-original_filename': file.name,
    },
  });

  await axiosInstance.post('/admin/assets/upload/confirm/', {
    asset_id: assetId,
  });

  return { assetId, uploadKey };
};

/**
 * App content artwork - served from the public CDN. Anything a user or agent
 * uploads (profile pictures, support attachments) must use uploadImageAsset,
 * which stays private.
 */
export const uploadArtworkAsset = (file, assetName) => uploadImageAsset(file, assetName, 'artwork');

/**
 * Upload a firmware binary via the same admin presigned-URL flow, scoped to
 * asset_type: 'firmware' (backend restricts these to .zip files).
 */
export const uploadFirmwareAsset = async (file, assetName) => {
  const { data: requestData } = await axiosInstance.post('/admin/assets/upload/request/', {
    filename: file.name,
    asset_type: 'firmware',
    content_type: file.type || 'application/zip',
    asset_name: assetName || file.name,
  });

  const presignedUrl = requestData.presignedUrl || requestData.presigned_url;
  const assetId = requestData.assetId || requestData.asset_id;
  const uploadKey = requestData.uploadKey || requestData.upload_key;

  await axios.put(presignedUrl, file, {
    headers: {
      ...presignHeaders(requestData, file.type || 'application/zip'),
      'x-amz-meta-asset_id': assetId,
      'x-amz-meta-original_filename': file.name,
    },
  });

  await axiosInstance.post('/admin/assets/upload/confirm/', {
    asset_id: assetId,
  });

  return { assetId, uploadKey };
};

// One presign call per asset type, capped at 50 files by the API. The returned
// order matches the files sent, so callers pair by index.
export const bulkPresignUploads = async (assetType, files) => {
  const { data } = await axiosInstance.post('/admin/assets/upload/request/bulk/', {
    asset_type: assetType,
    files: files.map((file) => ({
      filename: file.name,
      content_type: file.type || undefined,
      asset_name: file.name,
    })),
  });
  return data.uploads || [];
};

// Direct-to-R2 PUT for a presigned upload entry; onProgress receives 0-100.
export const putPresignedFile = async (upload, file, onProgress) => {
  const headers = presignHeaders(upload, file.type);
  await axios.put(upload.presigned_url, file, {
    headers: {
      ...headers,
      'x-amz-meta-asset_id': upload.asset_id,
      'x-amz-meta-original_filename': file.name,
    },
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
};

export const confirmAssetUpload = async (assetId) => {
  const { data } = await axiosInstance.post('/admin/assets/upload/confirm/', {
    asset_id: assetId,
  });
  return data;
};

// Batch processing state for up to 200 ids; ids the caller cannot see come back
// under `missing` rather than as rows.
export const fetchAssetStatuses = async (assetIds) => {
  const { data } = await axiosInstance.get('/admin/assets/status/', {
    params: { ids: assetIds.join(',') },
  });
  return data;
};
